import React, { useCallback } from 'react'
import { BaseDragHandle } from '@fs/zion-ui/quarks-authorized-use-only'
import { colors } from '@fs/zion-ui'
import { css } from '@emotion/core'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { getRotationMatrix, rotatePointWithMatrix } from './zoomPanDraggableUtils'

const HANDLE_SIZE = 24
const CONTAINER_MIN_SIZE = 24

const baseDragHandleCss = css`
  block-size: ${HANDLE_SIZE}px;
  border-radius: 4px;
  inline-size: ${HANDLE_SIZE}px;
  outline: none;
  position: absolute;

  ::after {
    background: ${colors.gray00};
    block-size: 100%;
    border: 2px solid ${colors.blue40};
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    content: '';
    display: block;
    inline-size: 100%;
  }

  &:focus-visible {
    outline: 4px solid ${colors.blue40};
  }
`

function getRotatedHorizontalResizeCursor(rotAngle, isLeft) {
  const baseAngle = isLeft ? 180 : 0
  const angle = (baseAngle + rotAngle) % 360

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
      <g transform="rotate(${angle},12,12)">
        <polyline
          points="1,12 4,9 4,12 20,12 20,9 23,12 20,15 20,12 4,12 4,15 1,12"
          fill="black"
          stroke="white"
          stroke-width="4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <polyline
          points="1,12 4,9 4,12 20,12 20,9 23,12 20,15 20,12 4,12 4,15 1,12"
          fill="black"
          stroke="black"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>
    </svg>
    `
}

export function HorizontalEdgeResizeHandles({ testIdPrefix, getBounds, maxSize, minSize, ...restProps }) {
  const sharedProps = {
    getBounds,
    maxSize,
    minSize,
    ...restProps,
  }

  return (
    <>
      <HorizontalEdgeResizeHandle
        {...sharedProps}
        isLeft
        data-testid={testIdPrefix ? `${testIdPrefix}-left` : undefined}
      />
      <HorizontalEdgeResizeHandle
        {...sharedProps}
        isRight
        data-testid={testIdPrefix ? `${testIdPrefix}-right` : undefined}
      />
    </>
  )
}

export default function HorizontalEdgeResizeHandle({
  isLeft,
  isRight,
  getBounds: containerGetBounds,
  maxSize,
  minSize = CONTAINER_MIN_SIZE,
  rotation = 0,
  ...props
}) {
  const [t] = useTranslation()

  if (!isLeft && !isRight) {
    isRight = true
  }

  const calculateNewDimensions = useCallback(
    ({ currentRect, delta, handleType }) => {
      const { x, y, width, height } = currentRect
      let newWidth = width
      let newX = x

      if (handleType === 'right') {
        newWidth = Math.max(minSize, width + delta.x)
      } else if (handleType === 'left') {
        newWidth = Math.max(minSize, width - delta.x)
        newX = x + width - newWidth
      }

      // Apply max size constraints
      if (maxSize) {
        if (newWidth > maxSize) {
          if (handleType === 'left') {
            newX = x + width - maxSize
          }
          newWidth = maxSize
        }
      }

      return { newWidth, newHeight: height, newX, newY: y }
    },
    [minSize, maxSize]
  )

  const applyBoundaryConstraints = useCallback(
    ({ boundary, dimensions }) => {
      if (!boundary) return dimensions

      let { newWidth, newX } = dimensions
      const { newY, newHeight } = dimensions
      const boundaryRight = boundary.x + boundary.width

      // Constrain to fit within boundary
      if (newX < boundary.x) {
        const diff = boundary.x - newX
        newX = boundary.x
        newWidth = Math.max(minSize, newWidth - diff)
      }
      if (newX + newWidth > boundaryRight) {
        newWidth = Math.max(minSize, boundaryRight - newX)
      }

      return { newWidth, newHeight, newX, newY }
    },
    [minSize]
  )

  const getHandleType = useCallback(() => {
    if (isLeft) return 'left'
    if (isRight) return 'right'
    return 'right'
  }, [isLeft, isRight])

  const getHandleLabel = useCallback(() => {
    if (isLeft) return t('HorizontalEdgeResizeHandle.keyboardDescription.left', 'Left Resize Handle')
    if (isRight) return t('HorizontalEdgeResizeHandle.keyboardDescription.right', 'Right Resize Handle')
    return t('HorizontalEdgeResizeHandle.keyboardDescription.right', 'Right Resize Handle')
  }, [isLeft, isRight, t])

  const handleResize = useCallback(
    ({ currentRect, delta }) => {
      const { x, y, width, height } = currentRect
      const rotationMatrix = getRotationMatrix(rotation)
      const inverseRotationMatrix = getRotationMatrix(-rotation)

      // Transform delta into local element axes
      const localDelta = rotatePointWithMatrix(delta, inverseRotationMatrix)

      // Identify anchor (opposite) edge in local space
      let anchorLocal = { x: 0, y: 0 }
      if (isRight) {
        anchorLocal = { x, y: y + height / 2 }
      } else if (isLeft) {
        anchorLocal = { x: x + width, y: y + height / 2 }
      }

      // Transform anchor to global space
      const cx = x + width / 2
      const cy = y + height / 2
      const anchorVec = { x: anchorLocal.x - cx, y: anchorLocal.y - cy }
      const rotatedAnchorVec = rotatePointWithMatrix(anchorVec, rotationMatrix)
      const anchorGlobal = {
        x: cx + rotatedAnchorVec.x,
        y: cy + rotatedAnchorVec.y,
      }

      // Get boundary
      const containerBounds = containerGetBounds()
      const containerBoundary = containerBounds?.boundary

      // Calculate new dimensions using localDelta
      const handleType = getHandleType()
      let dimensions = calculateNewDimensions({ currentRect, delta: localDelta, handleType })

      // Apply boundary constraints
      dimensions = applyBoundaryConstraints({ boundary: containerBoundary, dimensions })

      // Compute anchor offset in new local space
      let newAnchorOffset = { x: 0, y: 0 }
      if (isRight) {
        newAnchorOffset = { x: -dimensions.newWidth / 2, y: 0 }
      } else if (isLeft) {
        newAnchorOffset = { x: dimensions.newWidth / 2, y: 0 }
      }

      // Rotate new anchor offset by rotation
      const newAnchorOffsetRot = rotatePointWithMatrix(newAnchorOffset, rotationMatrix)

      // Set new center so anchor stays at anchorGlobal
      const finalCx = anchorGlobal.x - newAnchorOffsetRot.x
      const finalCy = anchorGlobal.y - newAnchorOffsetRot.y
      const finalX = finalCx - dimensions.newWidth / 2
      const finalY = finalCy - dimensions.newHeight / 2

      return {
        height: dimensions.newHeight,
        width: dimensions.newWidth,
        x: finalX,
        y: finalY,
      }
    },
    [isLeft, isRight, containerGetBounds, calculateNewDimensions, applyBoundaryConstraints, getHandleType, rotation]
  )

  const getBounds = useCallback(() => {
    return {
      min: { x: -Infinity, y: -Infinity },
      max: { x: Infinity, y: Infinity },
      minWidth: 0,
      minHeight: 0,
    }
  }, [])

  return (
    <BaseDragHandle
      {...props}
      css={baseDragHandleCss}
      cursor={`url("data:image/svg+xml;utf8,${encodeURIComponent(
        getRotatedHorizontalResizeCursor(rotation, !!isLeft)
      )}") 12 12, auto`}
      getBounds={getBounds}
      keyboardDescription={getHandleLabel()}
      onDragMove={(delta, currentRect) => handleResize({ currentRect, delta })}
      style={{
        ...(isLeft && { left: HANDLE_SIZE / -2, top: HANDLE_SIZE / -2 }),
        ...(isRight && { right: HANDLE_SIZE / -2, top: HANDLE_SIZE / -2 }),
        ...props.style,
      }}
      tabIndex={0}
      role="slider"
      data-move-handle
    />
  )
}

HorizontalEdgeResizeHandle.propTypes = {
  isLeft: PropTypes.bool,
  isRight: PropTypes.bool,
  getBounds: PropTypes.func,
  maxSize: PropTypes.number,
  minSize: PropTypes.number,
  rotation: PropTypes.number,
}
